import React from 'react';
import clsx from 'clsx';
import * as Headless from '@headlessui/react';

import { fieldChipClassName, splitAtFieldReferences } from './field-chips';

// The editor's chip sits on white paper in both themes; this one sits on the page.
const chipInFieldClassName = clsx(fieldChipClassName, 'dark:bg-sky-400/15 dark:text-sky-300');

/**
 * A one-line input that shows each [[field]] in its text as a chip. A copy of the text behind
 * the input draws it, scrolled with the input, while the input's own text is transparent, so
 * the caret and selection stay the input's. `className` sets the shared font and spacing.
 */
export const FieldReferenceInput = ({
  className,
  value,
  onScroll,
  onSelect,
  ...props
}: { className: string; value: string } & Omit<
  Headless.InputProps,
  'as' | 'className' | 'value'
>) => {
  const textCopy = React.useRef<HTMLSpanElement>(null);
  const followScroll = (input: HTMLInputElement) => {
    if (textCopy.current) textCopy.current.scrollLeft = input.scrollLeft;
  };
  return (
    <span className="relative block">
      <span
        ref={textCopy}
        aria-hidden
        className={clsx(
          className,
          'pointer-events-none absolute inset-0 overflow-hidden whitespace-pre text-neutral-900 dark:text-white'
        )}
      >
        {splitAtFieldReferences(value).map((part, index) =>
          part.isFieldReference ? (
            <span key={index} className={chipInFieldClassName}>
              {part.text}
            </span>
          ) : (
            part.text
          )
        )}
      </span>
      <Headless.Input
        {...props}
        value={value}
        onScroll={(event) => {
          followScroll(event.currentTarget);
          onScroll?.(event);
        }}
        onSelect={(event) => {
          followScroll(event.currentTarget);
          onSelect?.(event);
        }}
        className={clsx(
          className,
          'relative bg-transparent text-transparent caret-neutral-900 selection:bg-sky-500/30 dark:caret-white'
        )}
      />
    </span>
  );
};
